"use client"

import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { motion, useInView } from "framer-motion";
import Image from "next/image";


function Testimonial({ screenWidth, blok }) {
  const [current, setCurrent] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [animating, setAnimating] = useState(false);

  const allTestimonials = blok?.Testimonials?.filter(item => item.component == "testimonial-item")?.map(item => ({
    text: item.test,
    author: item.username,
    company: item.designation
  })) || []

  const companyLogos = blok?.CompanyLogos?.map(item => item.image.filename) || []
  const securinLogo = blok?.SecurinSmallLogo?.filename;

  const heading = blok?.Testimonials?.find(item => item.component == "Heading") || {}

  useEffect(() => {
    // Create a duplicated array of testimonials to ensure smooth infinite cycling
    const duplicatedTestimonials = [...allTestimonials];
    setTestimonials(duplicatedTestimonials);

    const interval = setInterval(() => {
      triggerSlideChange((current + 1) % allTestimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [current]);

  const triggerSlideChange = (index) => {
    setAnimating(true); // Trigger slide-out animation
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false); // Trigger slide-in animation
    }, 500); // Match animation duration (0.5s)
  };

  const handleDotClick = (index) => {
    if (index !== current) triggerSlideChange(index);
  };

  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <>
      <div
        className="absolute inset-0 flex items-center justify-center top-[800px] -z-10"
        aria-hidden="true"
      >
        <div className="w-[1920px] h-[500px] bg-white/70 blur-3xl rounded-full"></div>
      </div>
      <div className="flex flex-col  2xl:flex-row items-center justify-between lg:w-[100%] px-6 pt-20">
        {/* Left Section: Logo Layout with Animated Line */}
        <div className="relative flex-1 flex flex-col justify-center items-center mt-20">
          <div className="relative w-full h-full flex flex-col justify-between items-center max-w-[400px] mx-auto">
            <motion.div
              ref={ref}
              className="lg:top-[-28%] top-[-28%] absolute bg-[#48398C] lg:w-16 lg:h-16 p-2 rounded-[13px] flex justify-center
               items-center"
              initial={{ opacity: 0, y: -50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src={"/images/peopleIcon2.svg"}
                alt="Loop"
                className="w-full object-scale-down"
              />
            </motion.div>

            {/* Inline SVG with Animation */}
            <div className="top-[-13%] absolute h-[125%] lg:h-auto">
              <svg
                width="187"
                height="100%"
                viewBox="0 0 187 514"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="animated-svg"
              >
                <path
                  d="M93.6932 0V104C92.0266 110.5 85.7932 123.5 74.1932 123.5C62.5932 123.5 38.6932 123.5 28.1932 123.5C19.5266 124.5 2.19324 131.1 2.19324 149.5C2.19324 167.9 2.19324 206.833 2.19324 224C1.02657 234 4.59324 254 28.1932 254C51.7932 254 64.6933 254 68.1934 254C76.6933 253.667 93.6932 258.3 93.6932 279.5C93.6932 300.7 93.6932 343.333 93.6932 362C93.6817 362.15 93.6681 362.303 93.6521 362.459M93.6521 362.459C92.9763 369.091 88.2618 381.5 74.1932 381.5C59.7933 381.5 34.5267 381.5 23.6934 381.5C16 381.5 2.19324 388.2 2.19324 403C2.19324 417.8 2.19324 467.167 2.19324 490C2.19324 497.5 9 511 23.6934 511.5C38.4848 512.004 119.527 511.5 158.193 511.5C167.027 512 184.693 507.4 184.693 485C184.693 462.6 184.693 424.667 184.693 408.5C184.527 401.167 179.893 381.5 162.693 381.5C145.493 381.5 125.527 381.5 117.693 381.5C109.5 381.5 93.6521 377.626 93.6521 362.459Z"
                  stroke="#483888"
                  strokeWidth="3"
                />
                <path
                  d="M93.6934 104C94.86 110.667 101.593 124 119.193 124C136.793 124 151.86 124 157.193 124C166.193 124.167 184.193 129.8 184.193 151C184.193 172.2 184.193 205.167 184.193 219C184.527 230.333 180.993 253 164.193 253C147.393 253 127.193 253 119.193 253C110.693 253 93.6934 258.1 93.6934 278.5"
                  stroke="#483888"
                  strokeWidth="3"
                />
              </svg>
            </div>
            {/* Top Row: 2 Logos */}
            <div className="flex justify-center items-center space-x-4 -translate-y-12 lg:-translate-y-[55px]">
              <div
                className="lg:w-[169px] lg:h-[112.07px] w-28 p-2 h-16 mt-6 lg:mt-0 flex justify-center items-center rounded-[18px]"
                style={{
                  background:
                    "linear-gradient(130.1deg, #E9E0F6 3.61%, #F3E2F7 96.39%)",
                }}
              >
                <img
                  src={companyLogos[0]}
                  alt="Loop"
                  className="lg:w-24 lg:h-24 w-28 object-contain"
                />
              </div>
              <div
                className=" mt-6 lg:mt-0 lg:w-[169px] p-2 lg:h-[112.07px] w-28 h-16 flex justify-center items-center rounded-[18px]"
                style={{
                  background:
                    "linear-gradient(130.1deg, #F9CCD4 3.61%, #E5E4F9 96.39%)",
                }}
              >
                <img
                  src={companyLogos[1]}
                  alt="ChargePoint"
                  className="lg:w-30 lg:h-30 w-28 object-contain"
                />
              </div>
            </div>

            {/* Second Row: Centered Logo Row */}
            <div className="flex justify-center items-center space-x-2 lg:space-x-7 lg:-translate-y-[40px] -translate-y-3.5">
              <div
                className="lg:w-[169px] lg:h-[112.07px] p-2 w-28 h-16 flex justify-center items-center rounded-[18px] mt-[-20px] lg:mt-0"
                style={{
                  background:
                    "linear-gradient(130.1deg, #EFECFF 3.61%, #E5E4F9 96.39%)",
                }}
              >
                <img
                  src={companyLogos[2]}
                  alt="Changs"
                  className="lg:w-[99%] lg:h-20 w-28 object-contain"
                />
              </div>
              <div className="lg:w-36 lg:h-24 w-28 h-16 lg:mt-2 p-2 flex justify-center items-center rounded-[18px] mt-[-18px]">
                <img
                  src={securinLogo}
                  alt="SecurinS"
                  className="lg:w-20 lg:h-20 h-12 w-16 object-contain"
                />
              </div>
              <div
                className="lg:w-[169px] lg:h-[112.07px] w-28 h-16 flex p-2 justify-center items-center rounded-[18px] mt-[-20px] lg:mt-0"
                style={{
                  background:
                    "linear-gradient(130.1deg, #E9E0F6 3.61%, #F3E2F7 96.39%)",
                }}
              >
                <img
                  src={companyLogos[3]}
                  alt="Ping"
                  className="lg:w-[99%] lg:h-20 w-28 object-contain"
                />
              </div>
            </div>

            {/* Third Row: 2 Logos */}
            <div className="flex justify-center items-center space-x-4 lg:-translate-y-[21px] translate-y-[0px]">
              <div
                className="lg:w-[169px] lg:h-[112.07px] w-28 h-16 p-2 flex justify-center items-center rounded-[18px] "
                style={{
                  background:
                    "linear-gradient(130.1deg, #F9CCD4 3.61%, #E5E4F9 96.39%)",
                }}
              >
                <img
                  src={companyLogos[4]}
                  alt="Valmont"
                  className="lg:w-28 lg:h-20  w-full "
                />
              </div>
              <div
                className="lg:w-[169px] lg:h-[112.07px] w-28 h-16 p-2 flex justify-center items-center rounded-[18px]"
                style={{
                  background:
                    "linear-gradient(130.1deg, #EFECFF 3.61%, #E5E4F9 96.39%)",
                }}
              >
                <img
                  src={companyLogos[5]}
                  alt="People.ai"
                  className="lg:w-[99%] lg:h-20  w-28 object-contain"
                />
              </div>
            </div>

            {/* Bottom Row: Alliance Logo */}
            <div className="flex justify-center items-center translate-y-3.5 lg:-translate-y-[5px]">
              <div
                className="lg:w-[169px] lg:h-[112.07px] w-28 h-16 p-2 flex justify-center items-center rounded-[18px]"
                style={{
                  background:
                    "rgba(243, 226, 247, 1)"
                }}
              >
                <img
                  src={companyLogos[6]}
                  alt="Alliance"
                  className="lg:w-60 lg:h-30 w-28 object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Section: Testimonial */}
        <div className="flex flex-col items-center 2xl:items-start flex-1 lg:h-auto overflow-hidden mt-20 mb-14 relative h-full">
          {/* Heading */}
          <div className="text-center lg:text-left">
            <h2
              className="font-aileron text-[29px] lg:text-[64px] font-bold mb-2 bg-custom-radial 
      bg-clip-text text-transparent box-border"
            >
              {screenWidth >= 1700 ? (
                <>
                  <p className="mb-6 bg-custom-radial 
      bg-clip-text text-transparent leading-[135%]">{heading.header}</p>
                </>
              ) : (
                heading.header
              )}
            </h2>
          </div>

          {/* Testimonial Text */}
          <div
            className={`flex flex-col items-start  2xl:mt-0 2xl:items-start justify-center transition-all duration-500  pb-20 ${animating ? "slide-out-right" : "slide-in-left"
              }`}
            style={{ minHeight: "250px" }}
          >
            <p className="text-[18px] italic text-gray-700 mb-4 text-center lg:text-left lg:w-[80%]">
              {testimonials[current]?.text}
            </p>
            <h3 className="font-aileron text-[36px] font-bold text-center lg:text-left w-full pt-10" style={{ color: "#5928E1" }}>
              {testimonials[current]?.author}
            </h3>
            <p className="text-[24px] font-bold text-center lg:text-left w-full" style={{ color: "#9B7FEC" }}>
              {testimonials[current]?.company} {testimonials[current]?.position && ", " + testimonials[current]?.position}
            </p>
          </div>

          <style>{`
    @keyframes slideInLeft {
      from {
        transform: translateX(-100%);
      }
      
      Testimonial.propTypes = {
        screenWidth: PropTypes.number.isRequired
      };
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOutRight {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }

    .slide-in-left {
      animation: slideInLeft 0.5s ease-in-out;
    }

    .slide-out-right {
      animation: slideOutRight 0.5s ease-in-out;
    }

    @media (min-width: 1024px) {
      .translate-x-full {
        transform: translateX(100%);
        opacity: 0;
      }
      .translate-x-0 {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes draw {
      from {
        stroke-dashoffset: 3000;
      }
      to {
        stroke-dashoffset: 0;
      }
    }

    .animated-svg path {
      stroke-dasharray: 3000;
      stroke-dashoffset: 3000;
      animation: draw 4s ease-in-out forwards infinite;
    }
  `}</style>

          {/* Dots Navigation */}
          <div className="flex mt-10 space-x-2 justify-center lg:justify-start items absolute bottom-[-10px] z-10 h-20">
            <div className="flex gap-4">
              {testimonials.map((_, index) => (
                <button
                  aria-label="Change testimony"
                  key={index}
                  className={`w-3 h-3 rounded-full ${index === current ? "bg-purple-600" : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  onClick={() => handleDotClick(index)}
                />
              ))}
            </div>
          </div>
        </div>

      </div>
    </>
  );
}

Testimonial.propTypes = {
  screenWidth: PropTypes.number.isRequired
};

export default Testimonial;
